import dayjs from "dayjs";
import {
  ResponseBattleType as Battle,
  BattlesType,
  GetBattleHypeboardScoresResponse,
  GetBattleStatsResponse,
  HypeboardTimeframe,
  Position,
  ResponseBattleSide,
  ResponseBattleType,
} from "@dripit/types";

import isNil from "lodash.isnil";
import { BattleStatus, EnhancedBattleArtist, UserBattleStatus } from "@/types";
import { ChartProps } from "@/pages/battles/components/BattleChartUI";
import isEmpty from "lodash.isempty";
import { usdcToUsd } from "./convert";

type GetBattleStatus = (battle?: Partial<Battle>) => BattleStatus;

export const getBattleStatus: GetBattleStatus = ({
  start_date,
  end_date,
  winner_id,
  refundable,
} = {}) => {
  if (dayjs().isBefore(dayjs((start_date ?? 0) * 1000))) {
    return BattleStatus.Open;
  }

  if (dayjs().isBefore(dayjs((end_date ?? 0) * 1000))) {
    return BattleStatus.Pending;
  }

  if (dayjs().isAfter(dayjs((end_date ?? 0) * 1000))) {
    if (refundable) return BattleStatus.Canceled;
    if (!isNil(winner_id)) return BattleStatus.Completed;
  }

  return BattleStatus.Pending;
};

export const getBattleStatusKeyText = (battle?: Partial<Battle>) => {
  if (isNil(battle)) return "";

  if (dayjs().isBefore(dayjs((battle?.start_date ?? 0) * 1000))) {
    return "battles.starts-in";
  } else if (dayjs().isBefore(dayjs((battle?.end_date ?? 0) * 1000))) {
    return "battles.ends-in";
  }

  return "battles.ended";
};

type GetUserBattleStatus = (battle?: Partial<Battle>) => UserBattleStatus;

export const getUserBattleStatus: GetUserBattleStatus = (battle) => {
  const battleStatus = getBattleStatus(battle);
  const battleSidesWithBets =
    battle?.battleSides?.filter((side) => !isEmpty(side.positions)) ?? [];
  const hasBetOnSideThatWon = battleSidesWithBets.some(
    (side) => side?.artist_id === battle?.winner_id
  );

  if (battleStatus === BattleStatus.Open) return UserBattleStatus.Active;
  if (battleStatus === BattleStatus.Completed) {
    if (isEmpty(battleSidesWithBets)) return UserBattleStatus.Completed;
    if (hasBetOnSideThatWon) return UserBattleStatus.Won;
    return UserBattleStatus.Lost;
  }
  if (battleStatus === BattleStatus.Canceled) return UserBattleStatus.Canceled;

  return UserBattleStatus.Pending;
};

export const getBattleArtists = (
  sides?: ResponseBattleSide[],
  sorting?: "odds" | "hype"
): EnhancedBattleArtist[] => {
  const artists = sides?.reduce((artists, side) => {
    if (side?.artist) {
      artists.push({
        ...side.artist,
        id: side.artist?.id ?? side.artist_id,
        battleSideId: side.id,
        optionIndex: side.side_id,
        odds: side.odds ?? 0,
        amount: side.amount ?? 0,
      } as EnhancedBattleArtist);
    }
    return artists;
  }, [] as EnhancedBattleArtist[]);

  const sortedArtists =
    artists?.sort((a, b) => {
      const scoreA = a?.hypemeter_scores?.[0]?.score ?? 0;
      const scoreB = b?.hypemeter_scores?.[0]?.score ?? 0;
      const oddsA = a?.odds ?? 0;
      const oddsB = b?.odds ?? 0;
      return sorting === "odds" ? oddsB - oddsA : scoreB - scoreA;
    }) ?? [];

  return sortedArtists;
};

export const getArtistHypeMeter = (
  scores: EnhancedBattleArtist["hypemeter_scores"],
  timeframe: HypeboardTimeframe = HypeboardTimeframe["7D"]
) =>
  Math.floor(
    (scores?.find((obj) => obj.timeFrame === timeframe)?.score ?? 0) / 100
  );

export const getHypeMeterChartData = (
  scores?: GetBattleHypeboardScoresResponse
): ChartProps["chartData"] =>
  scores?.map((point) => ({
    date: point.date,
    ...point.data.reduce(
      (data, side) => ({
        ...data,
        [side.side_id]: Math.floor(Number(side.score?.toFixed(2) ?? 0) / 100),
      }),
      {} as Record<number, number>
    ),
  })) ?? [];

export const getHypeMeterChartConfig = (
  scores?: GetBattleHypeboardScoresResponse,
  artists?: EnhancedBattleArtist[]
): ChartProps["chartConfig"] => {
  if (!scores || !artists) return {};

  return (
    scores?.[0]?.data?.reduce(
      (data, point) => {
        const artistName = artists.find(
          (artist) => artist?.optionIndex === point.side_id
        )?.name;
        const dataLength = Object.keys(data).length;

        if (artistName) {
          return {
            ...data,
            [`${point.side_id}`]: {
              label: artistName,
              color: `hsl(var(--chart-${dataLength + 1}))`,
            },
          };
        }
        return data;
      },
      {} as Record<string, { label: string; color: string }>
    ) ?? {}
  );
};

export const getBattleStatsChartData = (
  stats?: GetBattleStatsResponse
): ChartProps["chartData"] =>
  stats?.map((point) => ({
    date: point.date,
    ...point.odds.reduce(
      (data, side) => ({
        ...data,
        [side.side_id]: Number(side.odds?.toFixed(2) ?? 0),
      }),
      {} as Record<number, number>
    ),
  })) ?? [];

export const getBattleStatsChartConfig = (
  stats?: GetBattleStatsResponse,
  artists?: EnhancedBattleArtist[]
): ChartProps["chartConfig"] => {
  if (!stats || !artists) return {};

  return (
    stats?.[0]?.odds?.reduce(
      (data, point) => {
        const artistName = artists.find(
          (artist) => artist.optionIndex === point.side_id
        )?.name;
        const dataLength = Object.keys(data).length;

        if (artistName) {
          return {
            ...data,
            [`${point.side_id}`]: {
              label: artistName,
              color: `hsl(var(--chart-${dataLength + 1}))`,
            },
          };
        }
        return data;
      },
      {} as Record<string, { label: string; color: string }>
    ) ?? {}
  );
};

const getBattleFee = (battleType?: BattlesType) => {
  const PLATFORM_FEE = 0.05; // 0.05%
  const MARKET_MAKER_INCENTIVE = 0.01; // 0.01%

  if (battleType === BattlesType.head2head_battle) {
    return PLATFORM_FEE + MARKET_MAKER_INCENTIVE;
  }
  if (battleType === BattlesType.hypeboard_battle) {
    return PLATFORM_FEE;
  }
  return 0;
};

export const getPotentialProfit = (
  battle: Partial<Battle>,
  side?: ResponseBattleSide,
  position?: Position | { amount: Position["amount"] }
) => {
  const fee = Number(getBattleFee(battle?.type)?.toFixed(2));
  const battleTotal = Number(battle?.total ?? 0);
  const sideTotal = Number(side?.amount ?? 0);
  const positionAmount = Number(position?.amount ?? 0);
  const battleTotalAfterBet = battleTotal + positionAmount;
  const sideTotalAfterBet = sideTotal + positionAmount;

  const potentialProfit =
    battleTotalAfterBet * (1.0 - fee) * (positionAmount / sideTotalAfterBet) -
    positionAmount;

  const profitAmount = usdcToUsd(potentialProfit);
  const profitPercentage =
    (profitAmount * 100) / usdcToUsd(positionAmount ?? 1);

  return {
    amount: isNaN(profitAmount) || profitAmount < 0 ? 0 : profitAmount,
    percentage:
      positionAmount <= 0 || isNaN(profitPercentage) || profitPercentage <= 0
        ? 0
        : profitPercentage,
  };
};

export const getBattleSide = (
  sideId?: number,
  battleSides?: ResponseBattleSide[]
) => battleSides?.find((side) => side.id === sideId);

export const getBattlePositionTitle = (
  battle: Partial<ResponseBattleType>,
  battleSideId?: ResponseBattleSide["id"]
) => {
  const artistName = battle?.battleSides?.find(
    (side) => side.id === battleSideId
  )?.artist?.name;
  if (isNil(battleSideId) || isNil(artistName) || isNil(battle?.name)) {
    return battle?.name;
  } else if (battle?.type === BattlesType.hypeboard_battle) {
    if (!battle?.name?.startsWith("Who will")) return battle?.name;
    return `Will ${artistName} ${battle?.name.split("Who will")[1]}`;
  } else if (battle?.type === BattlesType.head2head_battle) {
    const oponentName = battle?.battleSides?.find(
      (side) => side.id !== battleSideId
    )?.artist?.name;
    if (isNil(oponentName)) return battle?.name;
    return `Will ${artistName} win against ${oponentName}?`;
  }

  return battle?.name;
};
